import { SearchBar, type Column } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { userManagement, type Organisation } from "../../../api/userManagement.service";
import { DataTable } from "../../../components/DataTable/DataTable";
import { PaginationControls } from "../../../components/PaginationControls/PaginationControls";
import LoadingState from "../../../components/LoadingState";
import EmptyState from "../../../components/EmptyState";
import { ROUTES } from "../../../routes";
import styles from "../UserManagement.module.css";

type OrganisationListContext = {
  refetchTrigger?: number;
};

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case "active":
    case "verified":
      return styles.statusVerified;
    case "suspended":
      return styles.statusSuspended;
    default:
      return styles.statusPending;
  }
}

const COLUMNS: Column<Organisation>[] = [
  { header: "Name", accessor: "name" },
  {
    header: "Status",
    accessor: "status",
    render: (_value: unknown, org: Organisation) => (
      <span className={`${styles.statusBadge} ${getStatusBadgeClass(org.status)}`}>
        {org.status}
      </span>
    ),
  },
  {
    header: "Company ID",
    accessor: "companyId",
    render: (_value: unknown, org: Organisation) => org.companyId ?? "—",
  },
  {
    header: "Members",
    accessor: "memberCount",
    render: (_value: unknown, org: Organisation) =>
      org.memberCount !== undefined ? String(org.memberCount) : "—",
  },
];

function OrganisationList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useOutletContext<OrganisationListContext>();
  const [displayedOrgs, setDisplayedOrgs] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const itemsPerPage = Number(searchParams.get("pageSize")) || 25;

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function updateURL(updates: Record<string, string>) {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "") {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }

  function handleSearchChange(value: string) {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      updateURL({ search: value, page: "1" });
    }, 300);
  }

  function handlePageChange(page: number) {
    setCurrentPage(page);
    updateURL({ page: String(page) });
  }

  async function loadOrganisations() {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await userManagement.getOrganisations({
        search: searchTerm || undefined,
        limit: itemsPerPage,
        offset,
      });
      setDisplayedOrgs(response.organisations);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load organisations"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadOrganisations();
  }, [currentPage, itemsPerPage, searchParams.get("search")]);

  useEffect(() => {
    if (context.refetchTrigger !== undefined && context.refetchTrigger > 0) {
      loadOrganisations();
    }
  }, [context.refetchTrigger]);

  function handleRowClick(org: Organisation) {
    navigate(ROUTES.users.organisationDetail(org.id));
  }

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load"
        message={`Failed to load organisations: ${error.message}`}
      />
    );
  }

  return (
    <div>
      <div className={styles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search organisations..."
        />
      </div>

      <div className={styles.contentSection}>
        {loading ? (
          <LoadingState />
        ) : (
          <>
            <DataTable
              data={displayedOrgs}
              columns={COLUMNS}
              onRowClick={handleRowClick}
              emptyMessage="No organisations found."
              rowKey="id"
            />
            <PaginationControls
              currentPage={currentPage}
              totalPages={Math.ceil(total / itemsPerPage)}
              startIndex={(currentPage - 1) * itemsPerPage}
              endIndex={currentPage * itemsPerPage}
              totalCount={total}
              itemName="organisations"
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default OrganisationList;
